import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import AdminLayout from "../../../layouts/AdminLayout";
import DataTableLayout from "../../../layouts/DataTableLayout";
import DataTable from "../../../components/common/DataTable";
import FilterBar from "../../../components/common/FilterBar";
import Modal from "../../../components/ui/Modal";
import Button from "../../../components/ui/Button";
import { Plus } from "lucide-react";
import StatusBadge from "../../../components/ui/StatusBadge";
import styles from "./Support.module.css";
import Skeleton from "../../../components/ui/Skeleton";
import FormField from "../../../components/ui/FormField";
import TextInput from "../../../components/ui/TextInput";
import Textarea from "../../../components/ui/Textarea";

import {
  getAdminSupportTickets,
  getMySupportTickets,
  createSupportTicket,
  updateSupportTicketStatus,
} from "../../../services/supportTicketService";
import { getCurrentRoleKey } from "../../../services/authService";
import { supportTicketSchema, zodErrorToFieldErrors } from "../../../schemas/formSchemas";

const STATUS_OPTIONS = ["Open", "In_Progress", "Closed"];

const statusLabel = (value) => {
  if (value === "In_Progress") return "In Progress";
  return value || "-";
};

const RESIDENT_ROLES = ["user", "owner", "tenant", "family-member", "resident"];

const Support = ({ societyId, embedded = false }) => {
  const currentRole = getCurrentRoleKey();
  const isResident = RESIDENT_ROLES.includes(currentRole);

  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);

  const [filters, setFilters] = useState({ search: "", status: [] });
  const [appliedFilters, setAppliedFilters] = useState({ search: "", status: [] });

  const [openModal, setOpenModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [busyStatus, setBusyStatus] = useState(false);
  const [pickedStatus, setPickedStatus] = useState("Open");

  // Create Ticket Modal State
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createForm, setCreateForm] = useState({ subject: "", description: "" });

  const createErrors = useMemo(() => {
    const parsed = supportTicketSchema.safeParse(createForm);
    return parsed.success ? {} : zodErrorToFieldErrors(parsed.error);
  }, [createForm]);

  const handleCreateChange = (e) => {
    const { name, value } = e.target;
    setCreateForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const parsed = supportTicketSchema.safeParse(createForm);
    if (!parsed.success) {
      toast.error("Please fill required fields.");
      return;
    }

    setCreateBusy(true);
    try {
      const payload = await createSupportTicket(parsed.data);
      if (payload?.success) {
        toast.success("Support ticket created successfully.");
        setOpenCreateModal(false);
        setCreateForm({ subject: "", description: "" });
        await fetchTickets();
      } else {
        toast.error(payload?.message || "Unable to create support ticket.");
      }
    } catch (err) {
      toast.error(err?.message || "Unable to create support ticket.");
    } finally {
      setCreateBusy(false);
    }
  };

  const fetchTickets = async ({ status, search } = {}) => {
    setLoading(true);
    try {
      let data;
      if (isResident) {
        data = await getMySupportTickets();
      } else {
        data = await getAdminSupportTickets({ status, search, societyId });
      }
      setTickets(data?.tickets || []);
    } catch (err) {
      toast.error(err?.message || "Unable to load support tickets.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [societyId, isResident]);

  const filtered = useMemo(() => {
    const search = String(appliedFilters.search || "").trim().toLowerCase();
    const status = Array.isArray(appliedFilters.status) ? appliedFilters.status[0] : "";

    return tickets.filter((t) => {
      if (status && String(t.status || "") !== status) return false;
      if (!search) return true;
      return (
        String(t.ticket_id || "").includes(search) ||
        String(t.subject || "").toLowerCase().includes(search) ||
        String(t.description || "").toLowerCase().includes(search) ||
        String(t.users?.full_name || "").toLowerCase().includes(search) ||
        String(t.users?.email_id || "").toLowerCase().includes(search)
      );
    });
  }, [tickets, appliedFilters.search, appliedFilters.status]);

  const getStatusBadge = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "closed") {
      return <StatusBadge status="approved" label="Closed" />;
    }
    if (s === "in_progress") {
      return <StatusBadge status="pending" label="In Progress" />;
    }
    return <StatusBadge status="warning" label="Open" />;
  };

  const columns = [
    {
      header: "Ticket #",
      render: (row) => (
        <span style={{ fontFamily: "monospace", fontWeight: "700", color: "#334155" }}>
          #{row.ticket_id}
        </span>
      ),
    },
    {
      header: "Resident / Requester",
      render: (row) => (
        <div>
          <div style={{ fontWeight: "600", color: "#0f172a" }}>{row.users?.full_name || "-"}</div>
          {row.users?.email_id && (
            <div style={{ fontSize: "12px", color: "#64748b" }}>{row.users.email_id}</div>
          )}
        </div>
      ),
    },
    {
      header: "Subject",
      render: (row) => (
        <span style={{ fontWeight: "600", color: "#1e293b" }}>{row.subject || "-"}</span>
      ),
    },
    { header: "Status", render: (row) => getStatusBadge(row.status) },
    {
      header: "Created",
      render: (row) => (
        <span style={{ fontSize: "12.5px", color: "#64748b" }}>
          {row.created_at ? new Date(row.created_at).toLocaleString() : "-"}
        </span>
      ),
    },
    {
      header: "Action",
      render: (row) => (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            setSelected(row);
            setPickedStatus(row.status || "Open");
            setOpenModal(true);
          }}
        >
          {isResident ? "View Details" : "View / Update"}
        </Button>
      ),
    },
  ];

  const handleApplyFilters = () => setAppliedFilters(filters);
  const handleClearFilters = () => {
    const empty = { search: "", status: [] };
    setFilters(empty);
    setAppliedFilters(empty);
  };

  const handleUpdateStatus = async () => {
    if (!selected?.ticket_id) return;
    setBusyStatus(true);
    try {
      const data = await updateSupportTicketStatus(selected.ticket_id, pickedStatus);
      if (data?.success) {
        toast.success("Ticket status updated.");
        setOpenModal(false);
        await fetchTickets();
      } else {
        toast.error(data?.message || "Unable to update ticket.");
      }
    } catch (err) {
      toast.error(err?.message || "Unable to update ticket.");
    } finally {
      setBusyStatus(false);
    }
  };

  const content = (
    <>
      <DataTableLayout
        title="Support Tickets"
        filters={
          <FilterBar
            filtersConfig={[
              { key: "search", label: "Search", type: "text", placeholder: "Search tickets..." },
              { key: "status", label: "Status", options: STATUS_OPTIONS },
            ]}
            filters={filters}
            setFilters={setFilters}
            onApply={handleApplyFilters}
            onClear={handleClearFilters}
          />
        }
        actions={
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <Button variant="outline" onClick={() => fetchTickets()}>
              Refresh
            </Button>
            <Button icon={<Plus size={15} />} onClick={() => setOpenCreateModal(true)}>
              Create Ticket
            </Button>
          </div>
        }
      >
        {loading ? (
          <Skeleton variant="list" count={3} />
        ) : (
          <DataTable columns={columns} data={filtered} />
        )}
      </DataTableLayout>

      {/* View / Update Ticket Modal */}
      <Modal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title={selected ? `Ticket #${selected.ticket_id}` : "Ticket"}
      >
        {selected ? (
          <div className={styles.modalContent}>
            <div className={styles.details}>
              {!isResident && (
                <div className={styles.row}>
                  <div className={styles.label}>Resident</div>
                  <div className={styles.value}>
                    {selected.users?.full_name || "-"}{" "}
                    {selected.users?.email_id ? `(${selected.users.email_id})` : ""}
                  </div>
                </div>
              )}
              <div className={styles.row}>
                <div className={styles.label}>Subject</div>
                <div className={styles.value}>{selected.subject || "-"}</div>
              </div>
              <div className={styles.row}>
                <div className={styles.label}>Description</div>
                <div className={styles.description}>{selected.description || "-"}</div>
              </div>
              <div className={styles.row}>
                <div className={styles.label}>Status</div>
                <div className={styles.value}>{statusLabel(selected.status)}</div>
              </div>
              <div className={styles.row}>
                <div className={styles.label}>Created</div>
                <div className={styles.value}>
                  {selected.created_at ? new Date(selected.created_at).toLocaleString() : "-"}
                </div>
              </div>
            </div>

            {!isResident ? (
              <div className={styles.section}>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--color-text-strong)", marginBottom: "4px" }}>
                  Update Status
                </div>
                <div className={styles.statusButtons} style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "16px" }}>
                  {STATUS_OPTIONS.map((opt) => (
                    <Button
                      key={opt}
                      type="button"
                      variant={pickedStatus === opt ? "primary" : "outline"}
                      size="sm"
                      onClick={() => setPickedStatus(opt)}
                    >
                      {statusLabel(opt)}
                    </Button>
                  ))}
                </div>

                <div className={styles.actions}>
                  <Button onClick={handleUpdateStatus} disabled={busyStatus}>
                    {busyStatus ? "Updating..." : "Save Status"}
                  </Button>
                </div>
              </div>
            ) : (
              <div className={styles.actions} style={{ marginTop: "12px" }}>
                <Button variant="outline" onClick={() => setOpenModal(false)}>
                  Close
                </Button>
              </div>
            )}
          </div>
        ) : null}
      </Modal>

      {/* Create Ticket Modal */}
      <Modal
        isOpen={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        title="Create Support Ticket"
      >
        <form onSubmit={handleCreateSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <FormField label="Subject" required error={createErrors.subject}>
            <TextInput
              name="subject"
              value={createForm.subject}
              onChange={handleCreateChange}
              placeholder="e.g., Water leakage in corridor or elevator maintenance"
              required
              maxLength={255}
              error={Boolean(createErrors.subject)}
            />
          </FormField>

          <FormField label="Description" hint="Describe the issue, location, or request in detail">
            <Textarea
              name="description"
              value={createForm.description}
              onChange={handleCreateChange}
              placeholder="Please provide details about the problem..."
              rows={4}
            />
          </FormField>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", paddingTop: "8px" }}>
            <Button
              variant="outline"
              type="button"
              onClick={() => setOpenCreateModal(false)}
              disabled={createBusy}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={createBusy} loading={createBusy}>
              Submit Ticket
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );

  if (embedded) {
    return content;
  }

  return <AdminLayout>{content}</AdminLayout>;
};

export default Support;
